// src/components/dashboard/HowToUseSection-YouTube.tsx
// YouTube entegrasyonlu versiyon - Production için önerilen
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { PlayCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'

interface Tutorial {
  id: string
  title: string
  description: string
  youtubeId: string  // YouTube video ID
  duration?: string
}

// BURAYA YOUTUBE VİDEO ID'LERİNİZİ EKLEYİN
// Örnek: https://www.youtube.com/watch?v=dQw4w9WgXcQ → youtubeId: 'dQw4w9WgXcQ'
const tutorials: Tutorial[] = [
  {
    id: '1',
    title: 'Siteye nasıl üye olunur?',
    description: 'EarnBackHub platformuna üye olma sürecini adım adım öğrenin.',
    youtubeId: 'cxWcobo4upM',  // Buraya kendi video ID'nizi yazın
    duration: '1:19'
  },
  {
    id: '2',
    title: 'Üye olduktan sonra ne yapmalısınız?',
    description: 'Üyelik sonrası ilk adımlar, profil ayarları ve eklentiyi indirme.',
    youtubeId: '87OV-QaMJFk',
    duration: '0:59'
  },
  {
    id: '3',
    title: 'Siparişler nasıl güncellenir?',
    description: 'Amazon siparişlerinizi EarnBackHub ile nasıl senkronize ederek doğru bir rapora ulaşacağınızı öğrenin. Sorunsuz bir şekilde ödeme almak için sipariş güncellemeleri çok önemlidir.',
    youtubeId: 'M_oD2CYnVm4',
    duration: '0:46'
  },
  {
    id: '4',
    title: 'Ödeme yöntemi nasıl eklenir?',
    description: 'Hesabınıza ödeme almak için gerekli bilgilerin eklenmesi.',
    youtubeId: 'xSGT1J_29_0',
    duration: '1:21'
  },
  {
    id: '5',
    title: 'Manuel veya FBA sipariş nasıl verilir?',
    description: 'EarnBackHub sisteminde manuel ve FBA siparişlerin nasıl oluşturulacağını öğrenin.',
    youtubeId: 'QlkZJEUygjo',
    duration: '1:02'
  }
]

export default function HowToUseSection() {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const getThumbnailUrl = (youtubeId: string) => {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
  }

  const getVideoUrl = (youtubeId: string) => {
    return `https://www.youtube.com/embed/${youtubeId}`
  }

  const getWatchUrl = (youtubeId: string) => {
    return `https://www.youtube.com/watch?v=${youtubeId}`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="glass border-orange-200 shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <PlayCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-gray-800">
                  Nasıl Kullanılır?
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Video eğitimlerle sistemi hemen öğrenin
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>
      </motion.div>

      {/* Info Banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">
                💡 İpucu
              </h4>
              <p className="text-sm text-blue-800">
                Videoları tam ekran modunda izlemek için sağ alttaki tam ekran butonunu kullanabilirsiniz. 
                Videoları hızlandırmak için ayarlar (⚙️) menüsünden hızı değiştirebilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tutorials List */}
      <div className="space-y-4">
        {tutorials.map((tutorial, index) => (
          <motion.div
            key={tutorial.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + index * 0.1 }}
          >
            <Card className="glass border-orange-200 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                {/* Tutorial Header */}
                <button
                  onClick={() => toggleExpand(tutorial.id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-orange-50/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1 text-left">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-md">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-lg mb-1">
                        {tutorial.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {tutorial.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        {tutorial.duration && (
                          <span className="inline-flex items-center text-xs text-orange-600 font-medium">
                            ⏱ {tutorial.duration}
                          </span>
                        )}
                        <span className="inline-flex items-center text-xs text-gray-500">
                          📺 YouTube
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* YouTube Link */}
                    <a
                      href={getWatchUrl(tutorial.youtubeId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-2 hover:bg-orange-100 rounded-lg transition-colors"
                      title="YouTube'da Aç"
                    >
                      <ExternalLink className="w-4 h-4 text-gray-500" />
                    </a>
                    {/* Expand Icon */}
                    <div className="flex-shrink-0">
                      {expandedId === tutorial.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Video Player */}
                {expandedId === tutorial.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border-t border-orange-200"
                  >
                    <div className="p-6 bg-gray-50">
                      {/* YouTube Embed */}
                      <div className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-lg">
                        <iframe
                          src={getVideoUrl(tutorial.youtubeId)}
                          title={tutorial.title}
                          className="absolute inset-0 w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      
                      {/* Video Actions */}
                      <div className="mt-4 flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                          Videoda sorun mu var? 
                          <a 
                            href={getWatchUrl(tutorial.youtubeId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-600 hover:text-orange-700 font-medium ml-1"
                          >
                            YouTube'da izleyin →
                          </a>
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>


    </div>
  )
}